import { useState } from 'react';
import {
  Box, Typography, Stack, Button, List, ListItem, ListItemText,
  ListItemIcon, IconButton, Alert, CircularProgress, TextField,
} from '@mui/material';
import { AttachFile, Delete, InsertDriveFile, UploadFile } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attachmentService } from '@smartgrow/shared';
import type { AttachmentEntityType } from '@smartgrow/shared/types';

interface AttachmentsPanelProps {
  entityType: AttachmentEntityType;
  entityId: string;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB — generous for scans/photos, keeps base64 payloads reasonable

export const AttachmentsPanel = ({ entityType, entityId }: AttachmentsPanelProps) => {
  const qc = useQueryClient();
  const [pendingFile, setPendingFile] = useState<{ fileName: string; fileUrl: string } | null>(null);
  const [label, setLabel] = useState('');
  const [sizeError, setSizeError] = useState<string | null>(null);

  const { data: attachments, isLoading } = useQuery({
    queryKey: ['attachments', entityType, entityId],
    queryFn: () => attachmentService.getForEntity(entityType, entityId),
  });

  const uploadMutation = useMutation({
    mutationFn: attachmentService.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attachments', entityType, entityId] });
      setPendingFile(null);
      setLabel('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: attachmentService.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', entityType, entityId] }),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSizeError(null);
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSizeError(`"${file.name}" is too large (max 5MB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPendingFile({ fileName: file.name, fileUrl: ev.target?.result as string });
      setLabel((prev) => prev || file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = () => {
    if (!pendingFile || !label.trim()) return;
    uploadMutation.mutate({
      entityType,
      entityId,
      label: label.trim(),
      fileName: pendingFile.fileName,
      fileUrl: pendingFile.fileUrl,
    });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Attachments</Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
      ) : attachments && attachments.length > 0 ? (
        <List dense sx={{ mb: 2 }}>
          {attachments.map((a) => (
            <ListItem
              key={a.id}
              secondaryAction={
                <IconButton edge="end" size="small" onClick={() => deleteMutation.mutate(a.id)} disabled={deleteMutation.isPending}>
                  <Delete fontSize="small" />
                </IconButton>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}><InsertDriveFile fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={<a href={a.fileUrl} download={a.fileName} style={{ textDecoration: 'none', color: 'inherit' }}>{a.label}</a>}
                secondary={new Date(a.createdAt).toLocaleDateString('en-NG')}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No attachments yet.</Typography>
      )}

      {sizeError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSizeError(null)}>{sizeError}</Alert>}
      {uploadMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to upload attachment. Please try again.</Alert>}

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button component="label" size="small" variant="outlined" startIcon={<AttachFile />}>
          Choose File
          <input type="file" hidden onChange={handleFileSelect} accept="image/*,application/pdf" />
        </Button>
        {pendingFile && (
          <>
            <TextField
              size="small" label="Label" value={label}
              onChange={(e) => setLabel(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button
              size="small" variant="contained" startIcon={uploadMutation.isPending ? <CircularProgress size={16} /> : <UploadFile />}
              onClick={handleUpload} disabled={!label.trim() || uploadMutation.isPending}
            >
              Upload
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
};
